import { useCallback, useMemo, useRef } from "react";
import { Avatar, Divider, Select, message } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

import PropertyDocumentsModal from "components/property-documents-modal/property-documents-modal";
import routeNames from "constants/route-names";
import { useAuth } from "context/AuthContext";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { UserRoleValue } from "constants/field-values";
import { BRIEF_EMAIL_FREQUENCY_OPTIONS } from "constants/constants";
import { useUpdateUserByIdMutation } from "graphql/mutations/update-user-by-id.generated";
import { usePropertyDocumentsModal } from "hooks/usePropertyDocumentsModal";
import { useBrand } from "context/BrandContext";

interface ProfileContentProps {
	openAddPhotoModal: () => void;
	togglePopover: (value: boolean) => void;
	admin?: boolean;
}

const ProfileContent = (props: ProfileContentProps) => {
	const { openAddPhotoModal, togglePopover, admin = false } = props;
	const { loggedUserValue, updateLoggedUser } = useLoggedUser();
	const { logout } = useAuth();
	const { theme } = useBrand();

	const {
		propertyDocumentsModalOpen,
		selectedProperty,
		setPropertyDocumentsModalOpen,
		openModalWithProperty,
		handleOnSelectProperty
	} = usePropertyDocumentsModal();

	// Value to fall back to when a settings update does not go through.
	const previousBriefEmailFrequency = useRef<string | undefined>(undefined);

	const revertBriefEmailFrequency = useCallback(() => {
		updateLoggedUser({ brief_email_frequency: previousBriefEmailFrequency.current });
		message.error("Settings could not be updated.");
	}, [updateLoggedUser]);

	const [updateUserByIdMutation, { loading: updateUserByIdLoading }] = useUpdateUserByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: error => {
			console.log("Update Settings Error: ", error);
			revertBriefEmailFrequency();
		},
		onCompleted: data => {
			// No row comes back when the update matched nothing, which is not an Apollo error.
			if (!data.update_users_by_pk) {
				revertBriefEmailFrequency();
				return;
			}

			updateLoggedUser({ brief_email_frequency: data.update_users_by_pk.brief_email_frequency });
			message.success("Settings successfully updated!");
		}
	});

	const handleOnSelectBriefEmailFrequency = useCallback(
		(brief_email_frequency: string) => {
			// Show the selection right away; the mutation callbacks confirm or revert it.
			previousBriefEmailFrequency.current = loggedUserValue.brief_email_frequency;
			updateLoggedUser({ brief_email_frequency });

			updateUserByIdMutation({
				variables: { id: loggedUserValue.id, set: { brief_email_frequency } }
			});
		},
		[loggedUserValue.id, loggedUserValue.brief_email_frequency, updateLoggedUser, updateUserByIdMutation]
	);

	const permissionContent = useMemo(() => {
		switch (loggedUserValue.role) {
			case UserRoleValue.customer_manager:
				return (
					<>
						<span className="profile-info" style={{ marginBottom: "5px", marginTop: "11px" }}>
							{"Permission Level: Organization Manager"}
						</span>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								marginBottom: "25px"
							}}
						>
							<span className="profile-info permissions">{"Edit general information for your properties"}</span>
							<span className="profile-info permissions">{"Edit general information for your organization"}</span>
							<span className="profile-info permissions">{"Manage users within your organization"}</span>
							<span className="profile-info permissions">{"Manage property-level access"}</span>
						</div>
					</>
				);

			case UserRoleValue.customer_employee:
				return (
					<span className="profile-info" style={{ marginBottom: "25px", marginTop: "11px" }}>
						{"Permission Level: Organization User"}
					</span>
				);

			case UserRoleValue.vendor_manager:
				return (
					<>
						<span className="profile-info" style={{ marginBottom: "5px", marginTop: "11px" }}>
							{"Permission Level: Organization Manager"}
						</span>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								marginBottom: "25px"
							}}
						>
							<span className="profile-info permissions">
								{"Create and edit customer organizations and properties"}
							</span>
							<span className="profile-info permissions">{"Edit general information for your organization"}</span>
							<span className="profile-info permissions">
								{"Manage users within your organization and customer organizations"}
							</span>
							<span className="profile-info permissions">{"Manage property-level access"}</span>
							<span className="profile-info permissions">{"Manage your organization's device library"}</span>
						</div>
					</>
				);

			case UserRoleValue.vendor_employee:
				return (
					<>
						<span className="profile-info" style={{ marginBottom: "5px", marginTop: "11px" }}>
							{"Permission Level: Organization User"}
						</span>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								marginBottom: "25px"
							}}
						>
							<span className="profile-info permissions">{"Manage property-level products"}</span>
							<span className="profile-info permissions">{"Manage your organization's device library"}</span>
						</div>
					</>
				);

			case UserRoleValue.authority_manager:
				return (
					<>
						<span className="profile-info" style={{ marginBottom: "5px", marginTop: "11px" }}>
							{"Permission Level: AHJ Manager"}
						</span>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								marginBottom: "25px"
							}}
						>
							<span className="profile-info permissions">
								{"Access inspections and plan reviews for your jurisdiction"}
							</span>
							<span className="profile-info permissions">{"Manage users within your organization"}</span>
							<span className="profile-info permissions">{"Manage your organization profile"}</span>
						</div>
					</>
				);

			case UserRoleValue.authority_user:
				return (
					<>
						<span className="profile-info" style={{ marginBottom: "5px", marginTop: "11px" }}>
							{"Permission Level: AHJ Staff"}
						</span>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								marginBottom: "25px"
							}}
						>
							<span className="profile-info permissions">
								{"Access inspections and plan reviews for your jurisdiction"}
							</span>
						</div>
					</>
				);

			case UserRoleValue.admin_user:
				return (
					<>
						<span className="profile-info" style={{ marginBottom: "5px", marginTop: "11px" }}>
							{"Permission Level: Platform Admin"}
						</span>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								marginBottom: "25px"
							}}
						>
							<span className="profile-info permissions">{"Full access"}</span>
						</div>
					</>
				);

			case UserRoleValue.basic:
				return (
					<>
						<span className="profile-info" style={{ marginBottom: "5px", marginTop: "11px" }}>
							{"Permission Level: Basic"}
						</span>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								marginBottom: "25px"
							}}
						>
							<span className="profile-info permissions">View and create Guides</span>
							<span className="profile-info permissions">AI Assistant chat</span>
						</div>
					</>
				);

			default:
				return (
					<span className="profile-info" style={{ marginBottom: "25px", marginTop: "11px" }}>
						{"User Permission: All Access"}
					</span>
				);
		}
	}, [loggedUserValue.role]);

	return (
		<div className="profile-card">
			<div className="profile-card-header">
				<span className="profile-title first">{"My Profile"}</span>
				<img
					className="profile-close-icon"
					src={`${process.env.PUBLIC_URL}/assets/images/icons/x.svg`}
					onClick={() => togglePopover(false)}
				/>
			</div>
			<div className="my-profile-container">
				<div className="my-profile-avatar-container">
					<Avatar
						size={{ xs: 51, sm: 51, md: 102, lg: 102, xl: 102, xxl: 102 }}
						src={loggedUserValue.profile_photo || `${process.env.PUBLIC_URL}${theme.noProfilePhotoIcon}`}
						icon={<UserOutlined />}
					/>
					<span className="upload-image" onClick={openAddPhotoModal}>
						{"Edit Photo"}
					</span>
				</div>
				<Divider type="vertical" className="profile-vertical-divider" />
				<div className="my-profile-info-container">
					<span className="profile-name">{`${loggedUserValue.first_name} ${loggedUserValue.last_name}`}</span>
					<span className="profile-info" style={{ marginBottom: "7px" }}>
						{loggedUserValue.email}
					</span>

					{loggedUserValue.phone_number ? (
						<span className="profile-info" style={{ marginBottom: "19px" }}>
							{loggedUserValue.phone_number}
						</span>
					) : null}

					{/* {!admin && (
            <div className="profile-request-row">
              <Link
                to={{
                  pathname: routeNames.REQUEST_SERVICE,
                }}
                className="profile-request-link"
                rel="noopener"
              >
                Request Info Change
              </Link>
            </div>
          )} */}
				</div>
			</div>
			<Divider className="profile-divider" />

			<span className="profile-title">{"My Permissions"}</span>
			{permissionContent}

			<Divider className="profile-divider" />

			<span className="profile-title" style={{ marginBottom: "11px" }}>
				{"My Properties"}
			</span>
			{(loggedUserValue?.user_properties || []).map(element => {
				return (
					<span
						key={`profile-property-${element.property?.id}`}
						className="profile-info clickable"
						style={{ marginBottom: "6px" }}
						onClick={() => {
							openModalWithProperty(element.property);
						}}
					>
						{element.property?.name}
					</span>
				);
			})}

			{/* {!admin && (
        <div className="profile-request-row">
          <Link
            to={{
              pathname: routeNames.REQUEST_SERVICE,
            }}
            className="profile-request-link"
            style={{ marginBottom: "28px" }}
            rel="noopener"
          >
            Request Change
          </Link>
        </div>
      )} */}

			<Divider className="profile-divider" />

			<span className="profile-title" style={{ marginBottom: "11px" }}>
				{"Settings"}
			</span>
			<div className="profile-settings-container">
				<span className="profile-info">{"Property Brief Email Frequency"}</span>
				<Select
					className="profile-settings-select"
					popupClassName="profile-settings-select-popup"
					placeholder="Select One"
					value={loggedUserValue.brief_email_frequency || undefined}
					options={[...BRIEF_EMAIL_FREQUENCY_OPTIONS]}
					disabled={updateUserByIdLoading}
					onChange={handleOnSelectBriefEmailFrequency}
				/>
			</div>

			<Divider className="profile-divider" />

			<div className="logout-container">
				<span
					className="profile-logout"
					onClick={() => {
						if (logout) {
							logout(true);
						}
					}}
				>
					{"Log Out"}
				</span>
			</div>

			<PropertyDocumentsModal
				selectedProperty={selectedProperty}
				onSelectProperty={handleOnSelectProperty}
				isOpen={propertyDocumentsModalOpen}
				setIsOpen={setPropertyDocumentsModalOpen}
			/>
		</div>
	);
};

export default ProfileContent;
